import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { formatDate, getCategoryBySlug } from '@/lib/content';

const toneClass: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-800',
  accent: 'bg-accent-100 text-accent-900',
  secondary: 'bg-secondary-100 text-secondary-900',
};

interface ArticleCardProps {
  article: Article;
  variant?: 'vertical' | 'horizontal' | 'minimal';
}

export default function ArticleCard({ article, variant = 'vertical' }: ArticleCardProps) {
  const category = getCategoryBySlug(article.categorySlug);
  const badgeClass = category ? toneClass[category.tone] : toneClass.primary;

  if (variant === 'horizontal') {
    return (
      <article className="group flex flex-col gap-4 sm:flex-row">
        <Link
          to={`/insight/${article.id}`}
          className="block h-40 w-full shrink-0 overflow-hidden rounded-lg sm:h-28 sm:w-44"
        >
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - ${category?.name ?? '职场观察'}`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 font-label text-xs ${badgeClass}`}>
              {category?.name ?? '观察'}
            </span>
            <span className="font-label text-xs text-foreground-500">{formatDate(article.date)}</span>
          </div>
          <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
            <Link to={`/insight/${article.id}`} className="transition-colors hover:text-primary-600">
              {article.title}
            </Link>
          </h3>
          <p className="mt-1.5 line-clamp-2 hidden text-sm leading-relaxed text-foreground-600 sm:block">
            {article.excerpt}
          </p>
        </div>
      </article>
    );
  }

  if (variant === 'minimal') {
    return (
      <article className="group flex gap-3">
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 font-label text-xs font-semibold text-primary-700">
          {article.views > 9999 ? '热' : '新'}
        </div>
        <div className="min-w-0">
          <h4 className="font-heading text-sm font-semibold leading-snug text-foreground-900">
            <Link to={`/insight/${article.id}`} className="transition-colors hover:text-primary-600">
              {article.title}
            </Link>
          </h4>
          <p className="mt-1 font-label text-xs text-foreground-500">
            {category?.name ?? '观察'} · {formatDate(article.date)}
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/insight/${article.id}`} className="block h-48 w-full overflow-hidden">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - ${category?.name ?? '职场观察'}`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.05]"
        />
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <span className={`rounded-full px-2.5 py-0.5 font-label text-xs ${badgeClass}`}>
            {category?.name ?? '观察'}
          </span>
          <span className="font-label text-xs text-foreground-500">{article.readMinutes} 分钟阅读</span>
        </div>
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/insight/${article.id}`} className="transition-colors hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
          <span className="font-label text-xs text-foreground-500">{article.author}</span>
          <span className="font-label text-xs text-foreground-500">{formatDate(article.date)}</span>
        </div>
      </div>
    </article>
  );
}